import React, { useState, useEffect, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Chip, Typography, Box, Divider,
  LinearProgress, Collapse, IconButton, Tooltip
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import DomainMetricBoxes, { GRADE_COLORS } from './DomainMetricBoxes';
import { SectionHeader } from './utils';

const TIER_COLORS = { 0: '#4caf50', 1: '#ff9800', 2: '#f44336', 3: '#9c27b0' };
const TIER_LABELS = { 0: 'Pass', 1: 'Minor', 2: 'Moderate', 3: 'Severe' };

const CATEGORY_LABELS = {
  advertising_quality: 'Ad Quality',
  site_reputation: 'Reputation',
  mfa_detection: 'MFA Risk',
  traffic_quality: 'Traffic Quality',
  supply_path: 'Supply Path',
  brand_safety: 'Brand Safety',
};

// ─── Scan Progress Dialog (only shown during active scan) ──────────────────────

function ScanProgressDialog({ open, onClose, loading, progress }) {
  const progressEndRef = React.useRef(null);

  React.useEffect(() => {
    if (progressEndRef.current) {
      progressEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [progress]);

  const currentPhase = progress && progress.length > 0 ? progress[progress.length - 1].phase : null;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <AssessmentIcon />
        Scanning Domain...
      </DialogTitle>
      <DialogContent>
        <Box style={{ padding: '16px 0' }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="textSecondary">
              {currentPhase === 'fetch_domain' && 'Loading domain record...'}
              {currentPhase === 'external_data' && 'Fetching ads.txt and domain info...'}
              {currentPhase === 'traffic_data' && 'Querying historical traffic data...'}
              {currentPhase === 'evaluate_rules' && 'Evaluating quality rules...'}
              {currentPhase === 'build_summary' && 'Building summary...'}
              {!currentPhase && 'Scanning domain...'}
            </Typography>
          </Box>
          <LinearProgress style={{ marginBottom: '12px', height: 8, borderRadius: 4 }} />
          {progress.length > 0 && (
            <Box style={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '8px 12px', maxHeight: '150px', overflow: 'auto', fontSize: '12px', fontFamily: 'monospace' }}>
              {progress.map((p, i) => (
                <div key={i} style={{ padding: '2px 0', color: p.phase === 'complete' ? '#388e3c' : p.phase === 'evaluate_rules' ? '#1976d2' : '#555' }}>
                  [{p.phase}] {p.detail}
                </div>
              ))}
              <div ref={progressEndRef} />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Key Metrics (shown in Audit Summary) ────────────────────────────────────

function KeyMetrics({ summary }) {
  if (!summary) return null;

  const ds = summary.data_snapshots || {};
  const tranco = ds.tranco || {};
  const pageSpeed = ds.page_speed || {};
  const dns = ds.dns || {};
  const adsTxt = ds.ads_txt || {};

  const keyMetrics = [
    { label: 'Tranco Rank', value: tranco.ranked ? `#${tranco.rank.toLocaleString()}` : 'Unranked', color: tranco.ranked ? '#4caf50' : '#ff9800', tooltip: 'Tranco top-sites ranking. Ranked domains are more reputable. Top 100K = high trust.' },
    { label: 'DNS', value: dns.resolves ? 'OK' : 'Fail', color: dns.resolves ? '#4caf50' : '#f44336', tooltip: 'DNS resolution check. Fail means the domain does not resolve to an IP address.' },
    { label: 'Ads.txt', value: adsTxt.error ? 'Missing' : `${adsTxt.total_lines} lines`, color: adsTxt.error ? '#f44336' : '#4caf50', tooltip: 'Ads.txt file presence and size. Required for authorized digital selling. Missing = major red flag.' },
    { label: 'PageSpeed', value: pageSpeed.error ? 'N/A' : `${pageSpeed.performance_score}/100`, color: pageSpeed.error ? '#999' : pageSpeed.performance_score >= 50 ? '#4caf50' : pageSpeed.performance_score >= 30 ? '#ff9800' : '#f44336', tooltip: 'Google PageSpeed performance score (mobile). 50+ = good, 30-49 = needs work, <30 = poor.' },
  ];

  if (!pageSpeed.error && pageSpeed.seo_score !== null && pageSpeed.seo_score !== undefined) {
    keyMetrics.push({ label: 'SEO', value: `${pageSpeed.seo_score}/100`, color: pageSpeed.seo_score >= 80 ? '#4caf50' : pageSpeed.seo_score >= 50 ? '#ff9800' : '#f44336', tooltip: 'Google Lighthouse SEO score (mobile). Checks title, meta description, crawlability, and more. 80+ = good.' });
  }
  if (!pageSpeed.error && pageSpeed.lcp_ms) {
    keyMetrics.push({ label: 'LCP', value: `${(pageSpeed.lcp_ms / 1000).toFixed(1)}s`, color: pageSpeed.lcp_ms > 4000 ? '#f44336' : pageSpeed.lcp_ms > 2500 ? '#ff9800' : '#4caf50', tooltip: 'Largest Contentful Paint — time to render the largest visible element. <2.5s = good, 2.5-4s = needs improvement, >4s = poor.' });
  }
  if (!pageSpeed.error && pageSpeed.cls !== null && pageSpeed.cls !== undefined) {
    keyMetrics.push({ label: 'CLS', value: pageSpeed.cls.toFixed(2), color: pageSpeed.cls > 0.25 ? '#f44336' : pageSpeed.cls > 0.1 ? '#ff9800' : '#4caf50', tooltip: 'Cumulative Layout Shift — measures visual stability. <0.1 = good, 0.1-0.25 = needs improvement, >0.25 = poor. High CLS hurts ad viewability.' });
  }

  return (
    <Box style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '4px', marginBottom: '8px' }}>
      {keyMetrics.map(m => (
        <Tooltip key={m.label} title={m.tooltip || ''}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
            <Typography variant="caption" color="textSecondary" style={{ fontSize: '12px' }}>{m.label}:</Typography>
            <Typography variant="caption" style={{ fontSize: '12px', fontWeight: 'bold', color: m.color }}>{m.value}</Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

// ─── Site Info (shown under Audit Summary) ──────────────────────────────────

function SiteInfo({ summary }) {
  if (!summary) return null;

  const si = summary.site_info || {};
  const ds = summary.data_snapshots || {};
  const pageSpeed = ds.page_speed || {};
  const sourceLabel = si.source === 'pagespeed_seo' ? 'PageSpeed SEO audit'
    : si.source === 'direct_fetch' ? 'direct fetch' : 'web crawl';

  if (si.title || si.meta_description) {
    return (
      <div style={{ marginBottom: '12px', padding: '6px 10px', backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '4px', display: 'flex', gap: '10px', alignItems: 'center', maxWidth: '30%' }}>
        {si.og_image && (
          <img
            src={si.og_image.startsWith('http') ? si.og_image : `https://${summary.domain.name}${si.og_image}`}
            alt=""
            style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '3px', border: '1px solid #ddd', flexShrink: 0 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>via {sourceLabel}</div>
          {si.title && <div style={{ fontSize: '13px', fontWeight: 500, color: '#222' }}>{si.title}</div>}
          {si.meta_description && si.meta_description !== '(none)' && (
            <div style={{ fontSize: '11px', marginTop: '2px', color: '#555' }}>{si.meta_description}</div>
          )}
        </div>
      </div>
    );
  }

  if (si.source === 'pagespeed_seo') {
    return (
      <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
        <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>via PageSpeed SEO audit</div>
        <div style={{ fontSize: '13px', color: '#222' }}>
          Page title: {si.has_title ? 'Present' : 'Missing'} · Meta description: {si.has_meta_description ? 'Present' : 'Missing'}
        </div>
        {pageSpeed.final_url && (
          <div style={{ fontSize: '11px', color: '#555', marginTop: '4px' }}>
            Final URL: {pageSpeed.final_url}
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
          Site blocked direct crawl — only pass/fail checks available via PageSpeed
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '16px', padding: '10px 14px', backgroundColor: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '4px' }}>
      <div style={{ fontSize: '13px', color: '#bf360c', fontWeight: 500 }}>
        Site Info unavailable — could not fetch page content (site may block automated requests)
      </div>
    </div>
  );
}

// ─── Scan Details (inline on page) ─────────────────────────────────────────────

function ScanDetails({ summary }) {
  const [snapshotsOpen, setSnapshotsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!summary) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(summary, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div>
      {/* Category Breakdown */}
      <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Category Breakdown</Typography>
      {Object.entries(summary.category_scores || {}).map(([cat, data]) => (
        <Box key={cat} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <Typography variant="body2" style={{ width: '120px', fontWeight: 500 }}>{CATEGORY_LABELS[cat] || cat}</Typography>
          <Chip size="small" label={data.grade} style={{ backgroundColor: GRADE_COLORS[data.grade] || '#999', color: '#fff', fontSize: '11px', width: '32px' }} />
          <Box style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LinearProgress
              variant="determinate"
              value={data.score}
              style={{ flex: 1, height: 8, borderRadius: 4, backgroundColor: '#e0e0e0' }}
            />
            <Typography variant="body2" style={{ minWidth: '70px' }}>
              {data.score}/100 ({data.signals_count} signal{data.signals_count !== 1 ? 's' : ''})
            </Typography>
          </Box>
        </Box>
      ))}

      {/* Signals Table */}
      {summary.signals && summary.signals.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>Signals ({summary.signals.length})</Typography>
          <DataTable
            dense
            noHeader
            columns={[
              { name: 'Tier', width: '85px', cell: row => <Chip size="small" label={TIER_LABELS[row.tier] || `T${row.tier}`} style={{ backgroundColor: TIER_COLORS[row.tier] || '#999', color: '#fff', fontSize: '11px' }} /> },
              { name: 'Code', width: '80px', selector: row => row.code, sortable: true },
              { name: 'Category', width: '110px', cell: row => <Typography variant="caption">{CATEGORY_LABELS[row.category] || row.category}</Typography> },
              { name: 'Detail', selector: row => row.detail, wrap: true },
            ]}
            data={[...summary.signals].sort((a, b) => b.tier - a.tier)}
            pagination={false}
          />
        </>
      )}

      {/* Data Snapshots (collapsible) */}
      {summary.data_snapshots && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Box style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setSnapshotsOpen(!snapshotsOpen)}>
            <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Data Snapshots</Typography>
            <IconButton size="small">{snapshotsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
          </Box>
          <Collapse in={snapshotsOpen}>
            <Box style={{ backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px', fontSize: '12px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto', marginTop: '8px', color: '#333' }}>
              {JSON.stringify(summary.data_snapshots, null, 2)}
            </Box>
          </Collapse>
        </>
      )}

      {/* Copy Results */}
      <Divider style={{ margin: '12px 0' }} />
      <Button startIcon={<FileCopyIcon />} onClick={handleCopy} size="small">
        {copied ? 'Copied!' : 'Copy Results'}
      </Button>
    </div>
  );
}

// ─── Legend (bottom of audit tab) ────────────────────────────────────────────

const LEGEND_CATEGORIES = [
  { code: 'AQ', name: 'Advertising Quality', weight: '20%', description: 'Evaluates ads.txt compliance, ad density, network diversity, and bidder coverage.', source: 'Ads.txt file, web page analysis', standard: 'IAB requires ads.txt for authorized digital selling. 3+ active bidders and 2+ ad networks indicate healthy monetization.' },
  { code: 'SR', name: 'Site Reputation', weight: '20%', description: 'Tranco ranking, SSP audit status, DNS health, SSL availability, domain age, and PageSpeed performance.', source: 'Tranco top-sites list, DNS resolution, Google PageSpeed API, NoBid audit records', standard: 'Tranco top 100K = high trust. DNS must resolve. PageSpeed 50+ is acceptable for ad-supported sites.' },
  { code: 'MFA', name: 'MFA Detection', weight: '20%', description: 'Identifies Made-for-Advertising risk: bloated ads.txt, high reseller ratios, pagination abuse, content recommendation widgets, and ad-to-content ratio.', source: 'Ads.txt analysis, web page structure', standard: 'MFA sites typically have 400+ ads.txt lines, >3:1 reseller ratio, slideshow/pagination patterns, and minimal original content. Suppressed for Tranco-ranked domains.' },
  { code: 'TQ', name: 'Traffic Quality', weight: '15%', description: 'Historical IVT rates, fill rate performance, geographic anomalies, and revenue outliers.', source: 'NoBid historical data and analytics', standard: 'IVT rate <5% is clean. Fill rate >5% indicates demand. CPM between $0.10-$50 is normal range.' },
  { code: 'BS', name: 'Brand Safety', weight: '15%', description: 'Sensitive content keywords (adult, gambling, weapons), redirect behavior, empty pages, and malware indicators.', source: 'Web page content analysis, URL redirect checks', standard: 'GARM Brand Safety standards. No sensitive keywords, no domain redirects to different TLDs, no hidden iframes or crypto miners.' },
  { code: 'SPO', name: 'Supply Path', weight: '10%', description: 'Supply chain complexity including reseller-to-direct ratios and number of intermediary exchanges.', source: 'Ads.txt file analysis', standard: 'IAB SPO guidelines recommend minimizing intermediaries. Reseller:direct >5:1 or >50 exchanges indicates complex supply chain.' },
];

const LEGEND_GRADES = [
  { grade: 'A', range: '90-100', color: '#4caf50', meaning: 'Excellent — domain meets or exceeds industry standards across all categories' },
  { grade: 'B', range: '75-89', color: '#8bc34a', meaning: 'Good — minor issues detected, generally healthy domain' },
  { grade: 'C', range: '60-74', color: '#ff9800', meaning: 'Fair — moderate issues that should be addressed' },
  { grade: 'D', range: '40-59', color: '#f44336', meaning: 'Poor — significant issues impacting domain quality' },
  { grade: 'F', range: '0-39', color: '#9c27b0', meaning: 'Critical — severe issues requiring immediate attention' },
];

const LEGEND_TIERS = [
  { tier: 0, label: 'Pass', color: '#4caf50', deduction: 0, meaning: 'No issue detected' },
  { tier: 1, label: 'Minor', color: '#ff9800', deduction: 5, meaning: 'Low-severity concern, small score impact' },
  { tier: 2, label: 'Moderate', color: '#f44336', deduction: 15, meaning: 'Notable issue, moderate score impact' },
  { tier: 3, label: 'Severe', color: '#9c27b0', deduction: 30, meaning: 'Critical problem, major score impact' },
];

const LEGEND_METRICS = [
  { name: 'Tranco Rank', description: 'Position in the Tranco top-sites list (aggregated from Chrome, Majestic, Umbrella, Quantcast). Ranked domains are considered more reputable.', source: 'tranco-list.eu' },
  { name: 'Ads.txt', description: 'Authorized Digital Sellers file. Lists which exchanges/SSPs are authorized to sell inventory for this domain.', source: 'Domain root /ads.txt' },
  { name: 'PageSpeed', description: 'Google Lighthouse performance score on mobile. Measures loading speed, interactivity, and visual stability.', source: 'Google PageSpeed Insights API' },
  { name: 'LCP', description: 'Largest Contentful Paint — time to render the largest visible element. Industry standard: <2.5s good, 2.5-4s needs improvement, >4s poor.', source: 'Google Lighthouse' },
  { name: 'CLS', description: 'Cumulative Layout Shift — measures visual stability. Industry standard: <0.1 good, 0.1-0.25 needs improvement, >0.25 poor. High CLS hurts ad viewability.', source: 'Google Lighthouse' },
  { name: 'SEO Score', description: 'Google Lighthouse SEO audit score on mobile. Checks title tags, meta descriptions, crawlability, and structured data.', source: 'Google Lighthouse' },
];

function DomainScanLegend() {
  const [open, setOpen] = useState(false);

  const cellStyle = { padding: '4px 12px 4px 0', fontSize: '12px', verticalAlign: 'top' };
  const headerStyle = { ...cellStyle, fontWeight: 600, color: '#333', borderBottom: '1px solid #e0e0e0', paddingBottom: '6px' };
  const sectionTitle = { fontSize: '13px', fontWeight: 600, color: '#333', margin: '16px 0 8px 0' };

  return (
    <div style={{ marginTop: '24px' }}>
      <Box style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <HelpOutlineIcon style={{ fontSize: '16px', color: '#999', marginRight: '6px' }} />
        <Typography variant="subtitle2" color="textSecondary">Legend & Methodology</Typography>
        <IconButton size="small">{open ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
      </Box>
      <Collapse in={open}>
        <Box style={{ backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '16px 20px', marginTop: '8px', color: '#555' }}>

          {/* Grading Scale */}
          <div style={sectionTitle}>Grading Scale</div>
          <table style={{ borderCollapse: 'collapse', marginBottom: '8px' }}>
            <thead>
              <tr>
                <td style={headerStyle}>Grade</td>
                <td style={headerStyle}>Score Range</td>
                <td style={headerStyle}>Meaning</td>
              </tr>
            </thead>
            <tbody>
              {LEGEND_GRADES.map(g => (
                <tr key={g.grade}>
                  <td style={cellStyle}><Chip size="small" label={g.grade} style={{ backgroundColor: g.color, color: '#fff', fontSize: '11px', width: '28px' }} /></td>
                  <td style={cellStyle}>{g.range}</td>
                  <td style={cellStyle}>{g.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Signal Tiers */}
          <div style={sectionTitle}>Signal Tiers</div>
          <table style={{ borderCollapse: 'collapse', marginBottom: '8px' }}>
            <thead>
              <tr>
                <td style={headerStyle}>Tier</td>
                <td style={headerStyle}>Deduction</td>
                <td style={headerStyle}>Meaning</td>
              </tr>
            </thead>
            <tbody>
              {LEGEND_TIERS.map(t => (
                <tr key={t.tier}>
                  <td style={cellStyle}><Chip size="small" label={t.label} style={{ backgroundColor: t.color, color: '#fff', fontSize: '11px' }} /></td>
                  <td style={cellStyle}>{t.deduction === 0 ? 'None' : `-${t.deduction} pts`}</td>
                  <td style={cellStyle}>{t.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: '8px' }}>
            Category score = 100 minus sum of tier deductions. Overall score = weighted average of all category scores.
          </Typography>

          {/* Category Breakdown */}
          <div style={sectionTitle}>Scoring Categories</div>
          <table style={{ borderCollapse: 'collapse', marginBottom: '8px' }}>
            <thead>
              <tr>
                <td style={headerStyle}>Category</td>
                <td style={headerStyle}>Weight</td>
                <td style={headerStyle}>What It Measures</td>
                <td style={headerStyle}>Data Sources</td>
                <td style={headerStyle}>Industry Standard</td>
              </tr>
            </thead>
            <tbody>
              {LEGEND_CATEGORIES.map(c => (
                <tr key={c.code}>
                  <td style={{ ...cellStyle, fontWeight: 500, whiteSpace: 'nowrap' }}>{c.code} — {c.name}</td>
                  <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{c.weight}</td>
                  <td style={cellStyle}>{c.description}</td>
                  <td style={cellStyle}>{c.source}</td>
                  <td style={cellStyle}>{c.standard}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Key Metrics */}
          <div style={sectionTitle}>Key Metrics</div>
          <table style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <td style={headerStyle}>Metric</td>
                <td style={headerStyle}>Description</td>
                <td style={headerStyle}>Source</td>
              </tr>
            </thead>
            <tbody>
              {LEGEND_METRICS.map(m => (
                <tr key={m.name}>
                  <td style={{ ...cellStyle, fontWeight: 500, whiteSpace: 'nowrap' }}>{m.name}</td>
                  <td style={cellStyle}>{m.description}</td>
                  <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{m.source}</td>
                </tr>
              ))}
            </tbody>
          </table>

        </Box>
      </Collapse>
    </div>
  );
}

// ─── Main Panel ────────────────────────────────────────────────────────────────

export default function DomainAuditPanel({ record, summaryOnly = false }) {
  const domainCode = record && record.code;
  const domainName = record && record.name;
  const auditInfo = record && record.audit_info;

  const [latestScan, setLatestScan] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState([]);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [freshAuditInfo, setFreshAuditInfo] = useState(null);

  // Use freshAuditInfo (from just-completed scan) if available, otherwise record.audit_info
  const activeAuditInfo = freshAuditInfo || auditInfo;

  // Build a scan-like object for immediate rendering (metric boxes + site info)
  const auditSummary = activeAuditInfo ? {
    overall_score: activeAuditInfo.overall_score,
    overall_grade: activeAuditInfo.overall_grade,
    category_scores: activeAuditInfo.category_scores,
    site_info: activeAuditInfo.site_info || null,
    scan_timestamp: activeAuditInfo.scan_timestamp,
    domain: { code: domainCode, name: domainName },
  } : null;

  const displayScan = auditSummary;

  const fetchLatest = useCallback(() => {
    if (!domainCode) return;
    const url = `${window.nobid_endpoint}/domain_scan?domain_code=${domainCode}`;
    const token = localStorage.getItem('token');
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setLatestScan(data || null);
        window.dispatchEvent(new CustomEvent('domain-scan-updated'));
      })
      .catch(() => {
        setLatestScan(null);
      });
  }, [domainCode]);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  // Listen for scan updates from other DomainAuditPanel instances (e.g. Audit tab → Domain Settings tab)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && e.detail.domainCode === domainCode && e.detail.auditInfo) {
        setFreshAuditInfo(e.detail.auditInfo);
        fetchLatest();
      }
    };
    window.addEventListener('domain-scan-updated', handler);
    return () => window.removeEventListener('domain-scan-updated', handler);
  }, [domainCode, fetchLatest]);

  const handleScan = async () => {
    setScanProgress([]);
    setScanLoading(true);
    setProgressDialogOpen(true);

    const url = `${window.nobid_endpoint}/domain_scan`;
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          domain_code: domainCode,
          domain_name: domainName || 'Unknown',
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.event === 'result') {
              setLatestScan(parsed.data);
              // Update audit summary immediately so UI reflects the new scan without page reload
              const s = parsed.data;
              if (s && s.overall_score !== undefined) {
                const freshInfo = {
                  overall_score: s.overall_score,
                  overall_grade: s.overall_grade,
                  category_scores: s.category_scores,
                  site_info: s.site_info || null,
                  scan_timestamp: s.scan_timestamp,
                };
                setFreshAuditInfo(freshInfo);
                // Notify other DomainAuditPanel instances (e.g. Domain Settings tab)
                window.dispatchEvent(new CustomEvent('domain-scan-updated', { detail: { domainCode, auditInfo: freshInfo } }));
              }
              setScanLoading(false);
              setProgressDialogOpen(false);
            } else if (parsed.event === 'error') {
              setScanLoading(false);
              setProgressDialogOpen(false);
            } else {
              setScanProgress(prev => [...prev, parsed]);
            }
          } catch (e) { /* ignore parse errors */ }
        }
      }
    } catch (err) {
      setScanLoading(false);
      setProgressDialogOpen(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Audit Summary */}
      <SectionHeader title="Audit Summary" />
      {displayScan ? (
        <Box style={{ marginBottom: '8px' }}>
          <DomainMetricBoxes scan={displayScan} />
          {latestScan && <KeyMetrics summary={latestScan} />}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '8px' }}>
          No audit data available. Run a domain audit to generate a summary.
        </Typography>
      )}

      {/* Site Info */}
      {displayScan && <SiteInfo summary={displayScan} />}
      {displayScan && displayScan.scan_timestamp && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
          Last scanned: {new Date(displayScan.scan_timestamp).toLocaleString()}
        </Typography>
      )}

      {!summaryOnly && <>
        {/* Audit Details — scan button, signals, data snapshots */}
        <SectionHeader title="Audit Details" />

        {/* Run Scan Button */}
        <div style={{ marginBottom: '16px' }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<AssessmentIcon />}
            onClick={handleScan}
            disabled={scanLoading}
          >
            Run Domain Audit
          </Button>
        </div>

        {/* Scan Details (inline) */}
        {latestScan && <ScanDetails summary={latestScan} />}

        {/* Progress Dialog (only during active scan) */}
        <ScanProgressDialog
          open={progressDialogOpen}
          onClose={() => setProgressDialogOpen(false)}
          loading={scanLoading}
          progress={scanProgress}
        />

        {/* Legend & Methodology */}
        <DomainScanLegend />
      </>}
    </div>
  );
}
